// © Microsoft Corporation. All rights reserved.

import {
    CookieCategorization,
    CookieConsentProvider,
    CookieConsentStatus,
    GeneralCookieCategory,
    SameSiteValues,
    muidCookieName
} from "@msnews/cookie-contracts";
import {
    CookieConsentStatusNotSetProperly,
    CookieExpirationTooLong,
    CookieManagementStateNotInitialized,
    CookieNotCategorized,
    CookieSizeTooBig,
    InvalidCookie
} from "@msnews/diagnostics";
import { CookieManagementState, cookieManagementState } from "./CookieManagementState";
import { canUseDOM, getCookieString } from "@msnews/isomorphic-util";

import { Telemetry } from "@msnews/telemetry-contracts";

// Known list of "big" cookies for which we don't want to log an error.
const knownBigCookies = [
    "at",
    "at-bing",
    "eupubconsent-v2",
    "lt",
    "MSNRPSSecAuth"
];

// As per the spec for Cookies: https://curl.se/rfc/cookie_spec.html:
// NAME=VALUE - "This string is a sequence of characters excluding semi-colon, comma and white space."
const invalidCookieChars = [
    ";",
    ",",
    " "
];

/**
 * Returns the cookie content given a name
 *
 * @param name {string} - cookie name
 * @returns the value of the cookie or `null`, if the key is not found.
 */
export function getCookie(name: string): string {
    const cookieString = getCookieString();

    if (cookieString) {
        const re = new RegExp("\\b" + name + "\\s*=\\s*([^;]*)", "i");
        const match = re.exec(cookieString);
        return (match && match.length > 1 ? match[1] : null);
    }

    return null;
}

/**
 * Helper function that retrieves the MUID from the document.cookie
 * or uses the saved value if the cookie was retrieved for the current page instance
 */
export function getMuidCookie(): string {
    return getCookie(muidCookieName);
}

/**
 * Helper function used to set a cookie
 * When expiryDays is equal to -1 it will unset the cookie
 * @param {string} name Cookie name
 * @param {string} value Cookie value
 * @param {number} expiryDays Cookie expiry days
 * @param {string} domain Cookie domain
 * @param {string} path Cookie path
 * @param {boolean} secure Is it a https only cookie
 * @param {sameSiteValue} sameSiteValue The optional value to use for samesite cookie property
 * @returns {boolean} indicate if cookie saved successfully
 */
export function trySetCookie(
    name: string,
    value: string,
    expiryDays?: number,
    domain?: string,
    path?: string,
    secure?: boolean,
    sameSiteValue?: SameSiteValues
): boolean {

    const isInitialized = cookieManagementState.get(CookieManagementState.Initialized);

    if (!isInitialized) {
        Telemetry.sendAppErrorEvent(CookieManagementStateNotInitialized);
        return false;
    }

    if (expiryDays === undefined || expiryDays > 0) {
        if (!isSetCookiePermitted(name)) {
            return false;
        }

        if (value === undefined || value === null) {
            Telemetry.sendAppErrorEvent({
                ...InvalidCookie,
                message: `Cookie value is being set to either 'undefined' or 'null'. cookie name: "${name}", cookie value: "${value}"`
            });

            return false;
        }

        for (const invalidChar of invalidCookieChars) {
            if ((name.indexOf(invalidChar) > -1) || (value.indexOf(invalidChar) > -1)) {
                Telemetry.sendAppErrorEvent({
                    ...InvalidCookie,
                    // Can't log cookie value since it might contain PII
                    message: `Cookie name and/or value contains invalid character: '${invalidChar}'. cookie name: "${name}"`
                });

                return false;
            }
        }

        // For below scenarios, allow the cookie to be written but fire an app error for tracking purposes
        if (expiryDays > 365) {
            Telemetry.sendAppErrorEvent({
                ...CookieExpirationTooLong,
                // Can't log cookie value since it might contain PII
                message: `Cookie expiration time: ${expiryDays} days is greater than suggested max duration of 365 days (1 year). cookie name: "${name}"`
            });
        }

        const cookieSize =  name.length + (value && value.length || 0);
        if (cookieSize > 1000 && !knownBigCookies.includes(name)) {
            Telemetry.sendAppErrorEvent({
                ...CookieSizeTooBig,
                // Can't log cookie value since it might contain PII
                message: `Cookie size: "${cookieSize} Bytes" is too big (> 1000 Bytes). cookie name: "${name}"`
            });
        }
    }

    setAllowedCookie(name, value, expiryDays, domain, path, secure, sameSiteValue);
    return true;
}

/**
 * Helper function used to check if cookie setting is permitted
 * @param {string} cookieName Cookie name
 * @returns {boolean} True if cookie setting is permitted, false otherwise
 */
function isSetCookiePermitted(cookieName: string): boolean {

    if (!canUseDOM()) {
        return false;
    }

    const consentStatus =  cookieManagementState.get(CookieManagementState.CookieConsentStatus);
    const consentProvider =  cookieManagementState.get(CookieManagementState.CookieConsentProvider);
    const activeCookieCategories =  cookieManagementState.get(CookieManagementState.ActiveCookieCategories);

    let isSetAllowed = false;
    switch (consentProvider) {
        case CookieConsentProvider.MSCCBanner:
        case CookieConsentProvider.OneTrustBanner:
        case CookieConsentProvider.WidgetHost: {

            isSetAllowed = isCookieInActiveCategory(cookieName, activeCookieCategories);
            break;
        }
        default:
            isSetAllowed = consentStatus === CookieConsentStatus.NotRequired;

            if (!isSetAllowed) {
                Telemetry.sendAppErrorEvent({
                    ...CookieConsentStatusNotSetProperly,
                    message: `Consent status should be '2' (NotRequired), but is set to '${consentStatus}'`
                });
            }

            break;
    }

    return isSetAllowed;
}

/**
 * Check (case-sensitive check) if cookie is in active categories.
 * @param cookieName Cookie name
 * @param activeCategories The active (allowed) cookie category/categories.
 */
function isCookieInActiveCategory(cookieName: string, activeCategories: Array<GeneralCookieCategory>): boolean {

    // check the hardcoded cookie mapping in the active cookie categories
    for (const activeCategory of activeCategories) {
        const categoryCookies = CookieCategorization[activeCategory];

        if (categoryCookies.includes(cookieName)) {
            return true;
        }
    }

    // Check if the cookieName is present in the remaining cookie categories and fire an app error if not present
    let cookieCategorized = false;
    const remainingCategories = Object.keys(CookieCategorization).filter(category => !activeCategories.includes(GeneralCookieCategory[category]));

    // iterate through remaining category values
    for (const remainingCategory of remainingCategories) {
        const categoryCookies = CookieCategorization[remainingCategory];

        if (categoryCookies.includes(cookieName)) {
            cookieCategorized = true;
            break;
        }
    }

    if (!cookieCategorized) {
        Telemetry.sendAppErrorEvent({
            ...CookieNotCategorized,
            message: `Attempting to write a cookie: "${cookieName}" not present in the cookie category mapping.`
        });
    }

    return false;
}

/**
 * Helper function used to set a cookie
 * NOTE: THIS BYPASSES ANY GDPR CHECK AND HENCE ONLY REQUIRED FUNCTIONAL COOKIES SHOULD BE SET USING THIS.
 * When expiryDays is equal to -1 it will unset the cookie
 * * @param {string} name Cookie name
 * * @param {string} value Cookie value
 * * @param {number} expiryDays Cookie expiry days
 * * @param {string} domain Cookie domain
 * * @param {string} path Cookie path
 * * @param {boolean} secure Is it a https only cookie
 * * @param {SameSiteValues} sameSiteValue What value should we use for samesite? also sets secure.
 */
function setAllowedCookie(name: string, value: string, expiryDays?: number, domain?: string, path?: string, secure?: boolean, sameSiteValue?: SameSiteValues): void {
    let expiryDate;
    const builder = [name, "=", value];
    if (expiryDays < 0) {
        // Expires date format is supposed to be in GMT.
        expiryDate = "Fri, 31 Dec 1999 23:59:59 GMT";
    } else if (expiryDays) {
        const date = new Date();
        date.setTime(date.getTime() + (expiryDays * 86400000)); // 86400000 == 24*60*60*1000 (ms/day)
        expiryDate = date.toUTCString();
    }

    if (expiryDate) { builder.push(";expires=", expiryDate); }
    if (domain) { builder.push(";domain=", domain); }
    if (path) { builder.push(";path=", path); }
    if (secure) { builder.push(";secure"); }
    // Do we have a value to set for samesite?
    // NOTE: the browser default is "lax" so we won't set when we have that value.
    if (sameSiteValue && sameSiteValue !== SameSiteValues.Lax) {
        builder.push(";samesite=", sameSiteValue.toString());
        // If samesite is "none" and we have not already set "secure".
        if (sameSiteValue === SameSiteValues.None && !secure) {
            builder.push(";secure");
        }
    }

    if (document) {
        document.cookie = builder.join("");
    }
}